import styled from "@emotion/styled";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as React from "react";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { useClickOutside } from "hooks/useClickOutside";
import { withPx, withVar } from "themes/helpers";

type HeadersSpec = string[] | null | React.FC<{ column: number; row: number; header: boolean; columns: number }>;
interface ISettingsTableProps {
  columns: number;
  headers?: HeadersSpec;
  mobileBreakpoint?: string;
  columnSizes?: string;
  marginTop?: string;
  marginBottom?: string;
  children?: React.ReactNode;
}

const MobileHeaderBox = styled.div<{ mobileBreakpoint: string }>`
  display: block;
  font-weight: 600;
  padding-bottom: ${withVar(SpacingToken.Spacing3)};

  @media (min-width: ${withPx(ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile])}) {
    display: none;
  }
`;

interface ISettingsTableCounters {
  countColumn: () => number;
  countRow: () => number;
}

const SettingsTableContext = React.createContext<ISettingsTableProps & ISettingsTableCounters>({
  columns: 0,
  headers: null,
  countColumn: () => 0,
  countRow: () => 0,
  mobileBreakpoint: `${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px`,
});

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const headersRow = (headers: any, columns: any, mobileBreakpoint: any) => {
  const headerColumns: React.ReactNode[] = [];
  for (let i = 0; i < columns; i += 1) {
    headerColumns.push(
      <SettingsTableColumn key={`header-${i}`} className="header-column">
        {(_columns, _headers, rowNo, columnNo) => {
          if (Array.isArray(headers)) {
            return headers[i % columns];
          }

          if (headers === null) {
            return null;
          }

          const HeaderComponent = headers;
          return (
            <HeaderComponent
              mobileBreakpoint={mobileBreakpoint}
              row={rowNo}
              column={columnNo}
              columns={columns}
              header
            />
          );
        }}
      </SettingsTableColumn>
    );
  }

  return <SettingsTableRow className="header-row">{headerColumns}</SettingsTableRow>;
};

const SettingsTableGrid = styled.div<{
  columns: number;
  columnSizes: string;
  mobileBreakpoint: string;
  marginTop?: string;
  marginBottom?: string;
}>`
  font-size: 14px;
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  line-height: 1.4;
  text-align: left;
  font-weight: normal;
  vertical-align: middle;
  margin-top: ${(props): string => props.marginTop || withVar(SpacingToken.Spacing10)};
  margin-bottom: ${(props): string => props.marginBottom || withVar(SpacingToken.Spacing10)};
  display: grid;

  @media (min-width: ${withPx(ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile])}) {
    grid-template-columns: ${(props): string => props.columnSizes};
  }
`;

export const SettingsTable: React.FC<ISettingsTableProps> = ({
  children,
  columns,
  columnSizes,
  mobileBreakpoint = `${ResponsiveBreakpoint[ResponsiveBreakpointType.Tablet]}px`,
  headers = null,
  marginTop,
  marginBottom,
  ...props
}) => {
  const columnCounter: React.MutableRefObject<number> = useRef<number>(0);
  const rowCounter: React.MutableRefObject<number> = useRef<number>(0);
  const defaultColumnSizes = `repeat(${columns}, auto)`;
  const headersSet = headers !== null;

  useEffect(() => {
    rowCounter.current = 0;
  }, [columns]);

  useEffect(() => {
    columnCounter.current = 0;
  }, [columns]);

  const countColumn = useCallback(() => {
    columnCounter.current += 1;
    return columnCounter.current;
  }, []);

  const countRow = useCallback(() => {
    rowCounter.current += 1;
    return rowCounter.current;
  }, []);

  return (
    <SettingsTableContext.Provider value={{ columns, headers, countColumn, countRow, mobileBreakpoint }}>
      <SettingsTableGrid
        mobileBreakpoint={mobileBreakpoint}
        columns={columns}
        columnSizes={columnSizes || defaultColumnSizes}
        marginTop={marginTop}
        marginBottom={marginBottom}
        {...props}
      >
        {headersSet && headersRow(headers, columns, mobileBreakpoint)}
        {children}
      </SettingsTableGrid>
    </SettingsTableContext.Provider>
  );
};

interface ISettingsTableRowProps {
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children?: React.ReactNode | ((columns: number, headers: HeadersSpec, row: number) => React.ReactNode);
  highlight?: boolean;
}

const SettingsTableGridRow = styled.div<{ highlight: boolean }>`
  @media (min-width: ${withPx(ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile])}) {
    display: contents;

    &.header-row {
      font-weight: 600;
    }

    & .settings-table-column {
      transition: background-color 0.2s ease-in-out;
    }

    &:hover .settings-table-column,
    &:active .settings-table-column,
    &:focus-within .settings-table-column,
    &:focus .settings-table-column {
      background-color: var(${DesignToken.SurfaceSecondaryDefault});
      ${({ highlight }): string =>
        highlight ? `background-color: var(${DesignToken.SurfaceAccentEmphasisMinInfo});` : ""}
    }

    & .settings-table-column {
      ${({ highlight }): string =>
        highlight ? `background-color: var(${DesignToken.SurfaceAccentEmphasisLowInfo});` : ""}
    }
  }
`;

const SettingsTableRowContext = React.createContext(0);

export const SettingsTableRow: React.FC<ISettingsTableRowProps> = ({ children, onClick, highlight, ...props }) => {
  const [isHighlighted, setIsHighlighted] = useState(highlight ?? false);
  const rowRef = useRef<HTMLDivElement>(null);
  const tableContext = useContext(SettingsTableContext);
  const rowNumber = useRef(tableContext.countRow());
  const { columns, headers } = tableContext;
  useClickOutside({ elementRef: rowRef, onClickOutside: () => setIsHighlighted(false) });
  useEffect(() => {
    rowNumber.current = tableContext.countRow();
  }, [tableContext, tableContext.columns]);

  return (
    <SettingsTableRowContext.Provider value={rowNumber.current}>
      <SettingsTableGridRow {...props} tabIndex={-1} onClick={onClick} ref={rowRef} highlight={isHighlighted}>
        {typeof children === "function" ? children(columns, headers ?? [], rowNumber.current) : children}
      </SettingsTableGridRow>
    </SettingsTableRowContext.Provider>
  );
};

interface ISettingsTableColumnProps {
  className?: string;
  noHeader?: boolean;
  paddingMobile?: string;
  paddingDesktop?: string;
  children?:
    | React.ReactNode
    | ((columns: number, headers: HeadersSpec, row: number, column: number) => React.ReactNode);
}

const SettingsTableGridElement = styled.div<{
  mobileBreakpoint: string;
  column: number;
  columns: number;
  noHeader?: boolean;
  paddingDesktop?: string;
  paddingMobile?: string;
}>`
  &:nth-of-type(${(props): number => props.columns}n + 1) {
    border-top: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  }

  ${(props): string => {
    if (!props.noHeader) {
      return `
        &::before {
          display: block;
          content: attr(data-header);
          padding-bottom: ${withVar(SpacingToken.Spacing3)};
          font-weight: 600;
        }
     `;
    }
    return "";
  }}

  &.header-column {
    display: none;
    color: ${withVar(DesignToken.ContentBasicSecondary)};
    font-size: 12px;
  }

  padding: ${(props): string =>
    props.paddingMobile || `${withVar(SpacingToken.Spacing3)} ${withVar(SpacingToken.Spacing5)}`};

  @media (min-width: ${(props): string => props.mobileBreakpoint}) {
    &.header-column {
      display: block;
      padding-top: 0;
      padding-bottom: ${withVar(SpacingToken.Spacing2)};
      border-top: 0;
    }

    border-top: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
    padding: ${(props): string =>
      props.paddingDesktop || `${withVar(SpacingToken.Spacing7)} ${withVar(SpacingToken.Spacing5)}`};

    &::before {
      display: none;
    }
  }
`;

export const SettingsTableColumn: React.FC<ISettingsTableColumnProps> = ({
  children,
  noHeader = false,
  className: userClassName,
  ...props
}) => {
  const tableContext = useContext(SettingsTableContext);
  const rowNumber = useContext(SettingsTableRowContext);
  const columnNumber = useRef(tableContext.countColumn());
  const { columns, headers } = tableContext;
  useEffect(() => {
    columnNumber.current = tableContext.countColumn();
  }, [tableContext, tableContext.columns]);

  const isArrayHeader = Array.isArray(headers);
  let headerContent = null;
  let componentHeader = false;
  if (headers === null) {
    headerContent = null;
  } else if (isArrayHeader) {
    headerContent =
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      (tableContext.headers && tableContext.headers[(columnNumber.current - 1) % tableContext.columns]) || "";
  } else {
    const Headers = headers as React.FC<{ column: number; row: number; header: boolean; columns: number }>;
    headerContent = (
      // @ts-expect-error TS(2322)
      <MobileHeaderBox mobileBreakpoint={tableContext.mobileBreakpoint}>
        <Headers column={columnNumber.current} row={rowNumber} columns={tableContext.columns} header={false} />
      </MobileHeaderBox>
    );
    componentHeader = true;
  }

  const headerProps =
    noHeader || headerContent === null || componentHeader ? { noHeader: true } : { "data-header": headerContent };

  return (
    <SettingsTableGridElement
      {...headerProps}
      columns={tableContext.columns}
      column={columnNumber.current}
      className={userClassName || "settings-table-column"}
      // @ts-expect-error TS(2322)
      mobileBreakpoint={tableContext.mobileBreakpoint}
      {...props}
    >
      {!noHeader && componentHeader && headerContent}
      {typeof children === "function" ? children(columns, headers ?? [], rowNumber, columnNumber.current) : children}
    </SettingsTableGridElement>
  );
};
